import React, { useState } from 'react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line
} from 'recharts';
import './MiniChart.css';

// Benchmark configuration
const BENCHMARK_CONFIG = {
  sp500: { label: 'S&P 500', color: '#10B981' },
  nasdaq: { label: 'NASDAQ', color: '#48D1CC' },  // Turquoise for better brownie theme support
  russell2000: { label: 'Russell 2000', color: '#FF8A00' },  // Orange for better brownie theme support
  dax: { label: 'DAX', color: '#F59E0B' },
  stoxx600: { label: 'STOXX 600', color: '#EC4899' },
  msciWorld: { label: 'MSCI World', color: '#14B8A6' },
  bitcoin: { label: 'Bitcoin', color: '#F7931A' },  // Bitcoin orange
};

/**
 * Reusable Mini Chart Component for Tab-Specific Visualizations
 *
 * @param {Object} props
 * @param {Array} props.data - Chart data array with { date, value } structure
 * @param {String} props.title - Chart title
 * @param {String} props.dataKey - Key for the value in data objects (default: 'value')
 * @param {String} props.color - Primary chart color (hex)
 * @param {String} props.type - Chart type: 'area' or 'line' (default: 'area')
 * @param {Boolean} props.loading - Loading state
 * @param {String} props.error - Error message
 * @param {String} props.emptyMessage - Message when no data available
 * @param {Function} props.valueFormatter - Custom formatter for values (tooltip/axis)
 * @param {Number} props.height - Chart height in pixels (default: 200)
 * @param {Object} props.benchmarks - Benchmarks object with keys: sp500, nasdaq, russell2000, dax, stoxx600, msciWorld
 * @param {Array} props.benchmarkData - Legacy: Optional single benchmark data (backward compatibility)
 * @param {String} props.benchmarkLabel - Legacy: Label for single benchmark line (backward compatibility)
 * @param {Object} props.comparison - Comparison metrics { portfolioReturn, sp500Return, outperformance }
 */
const MiniChart = ({
  data = [],
  title = 'Trend',
  dataKey = 'value',
  color = 'var(--color-primary-500)',  // Use theme primary color instead of hardcoded purple
  type = 'area',
  loading = false,
  error = null,
  emptyMessage = 'No data available yet',
  valueFormatter = (value) => `€${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
  height = 200,
  benchmarks = null,
  benchmarkData = null,
  benchmarkLabel = 'S&P 500',
  comparison = null
}) => {
  // State to track which benchmarks are visible
  const [visibleBenchmarks, setVisibleBenchmarks] = useState({
    sp500: true,
    nasdaq: false,
    russell2000: false,
    dax: false,
    stoxx600: false,
    msciWorld: true,
    bitcoin: true,
  });

  // Toggle benchmark visibility
  const toggleBenchmark = (key) => {
    setVisibleBenchmarks(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Merge portfolio and benchmark data for multi-line chart
  const mergedData = React.useMemo(() => {
    // Start with portfolio data
    const dataMap = new Map(data.map(d => [d.date, d]));
    const allDates = new Set(data.map(d => d.date));

    // If using new benchmarks object
    if (benchmarks && Object.keys(benchmarks).length > 0) {
      // Add benchmark dates
      Object.values(benchmarks).forEach(benchmarkArray => {
        if (benchmarkArray && Array.isArray(benchmarkArray)) {
          benchmarkArray.forEach(d => allDates.add(d.date));
        }
      });

      // Create benchmark maps
      const benchmarkMaps = {};
      Object.entries(benchmarks).forEach(([key, benchmarkArray]) => {
        if (benchmarkArray && Array.isArray(benchmarkArray)) {
          benchmarkMaps[key] = new Map(benchmarkArray.map(d => [d.date, d.value]));
        } else {
          benchmarkMaps[key] = new Map();
        }
      });

      // Merge all data
      return Array.from(allDates)
        .map(date => {
          const point = {
            date,
            value: dataMap.get(date)?.value || null
          };

          // Add all benchmark values
          Object.keys(BENCHMARK_CONFIG).forEach(key => {
            point[key] = benchmarkMaps[key]?.get(date) || null;
          });

          return point;
        })
        .filter(d => d.value !== null || Object.keys(BENCHMARK_CONFIG).some(k => d[k] !== null))
        .sort((a, b) => a.date.localeCompare(b.date));
    }

    // Legacy: single benchmark support
    if (benchmarkData && benchmarkData.length > 0) {
      const benchmarkMap = new Map(benchmarkData.map(d => [d.date, d.value]));
      benchmarkData.forEach(d => allDates.add(d.date));

      return Array.from(allDates)
        .map(date => ({
          date,
          value: dataMap.get(date)?.value || null,
          benchmarkValue: benchmarkMap.get(date) || null
        }))
        .filter(d => d.value !== null || d.benchmarkValue !== null)
        .sort((a, b) => a.date.localeCompare(b.date));
    }

    // No benchmarks, just return portfolio data
    return data;
  }, [data, benchmarks, benchmarkData]);

  // Calculate performance for all benchmarks (regardless of visibility state)
  const benchmarkPerformances = React.useMemo(() => {
    if (!benchmarks || Object.keys(benchmarks).length === 0) return {};

    const performances = {};

    Object.entries(BENCHMARK_CONFIG).forEach(([key, config]) => {
      if (!benchmarks[key] || benchmarks[key].length === 0) {
        return;
      }

      const benchmarkStart = benchmarks[key][0].value;
      const benchmarkEnd = benchmarks[key][benchmarks[key].length - 1].value;

      if (benchmarkStart > 0) {
        const returnPct = ((benchmarkEnd - benchmarkStart) / benchmarkStart) * 100;
        performances[key] = {
          label: config.label,
          color: config.color,
          return: returnPct
        };
      }
    });

    return performances;
  }, [benchmarks]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="mini-chart-tooltip">
          <p className="tooltip-date">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="tooltip-value" style={{ color: entry.color }}>
              {entry.name}: {valueFormatter(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Calculate Y-axis domain for relative scaling (including visible benchmarks)
  const getYAxisDomain = () => {
    if (mergedData.length === 0) return [0, 'auto'];

    // Collect all values (portfolio + visible benchmarks)
    const allValues = mergedData.reduce((acc, d) => {
      if (d.value !== null) acc.push(d.value);

      // New multi-benchmark mode
      if (benchmarks) {
        Object.keys(BENCHMARK_CONFIG).forEach(key => {
          if (visibleBenchmarks[key] && d[key] !== null) {
            acc.push(d[key]);
          }
        });
      }

      // Legacy single benchmark mode
      if (d.benchmarkValue !== null) acc.push(d.benchmarkValue);

      return acc;
    }, []);

    if (allValues.length === 0) return [0, 'auto'];

    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    const range = maxValue - minValue;

    // Add 10% padding above and below for better visualization
    const padding = range * 0.1;
    const domainMin = Math.max(0, minValue - padding); // Don't go below 0
    const domainMax = maxValue + padding;

    return [domainMin, domainMax];
  };

  // Format Y-axis values
  const formatYAxis = (value) => {
    if (value >= 1000000) {
      return `€${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `€${(value / 1000).toFixed(0)}k`;
    }
    return `€${value}`;
  };

  // Format X-axis dates
  const formatXAxis = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="mini-chart-container">
        <div className="mini-chart-header">
          <h3>{title}</h3>
        </div>
        <div className="mini-chart-loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mini-chart-container">
        <div className="mini-chart-header">
          <h3>{title}</h3>
        </div>
        <div className="mini-chart-error">{error}</div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="mini-chart-container">
        <div className="mini-chart-header">
          <h3>{title}</h3>
        </div>
        <div className="mini-chart-empty">{emptyMessage}</div>
      </div>
    );
  }

  // Calculate trend
  const firstValue = data[0]?.[dataKey] || 0;
  const lastValue = data[data.length - 1]?.[dataKey] || 0;
  const change = lastValue - firstValue;
  const percentageChange = firstValue !== 0 ? ((change / firstValue) * 100).toFixed(2) : null;
  const isPositive = change >= 0;

  return (
    <div className="mini-chart-container">
      <div className="mini-chart-header">
        <h3>{title}</h3>
        <div className="mini-chart-summary">
          <span className="current-value">{valueFormatter(lastValue)}</span>
          <span className={`change ${isPositive ? 'positive' : 'negative'}`}>
            {isPositive ? '+' : ''}{change.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            {percentageChange !== null ? ` (${isPositive ? '+' : ''}${percentageChange}%)` : ' (New)'}
          </span>
        </div>
      </div>

      {/* Benchmark Performance Metrics */}
      {Object.keys(benchmarkPerformances).length > 0 && (
        <div className="mini-chart-comparison">
          {Object.entries(benchmarkPerformances).map(([key, perf]) => (
            <div key={key} className="benchmark-performance">
              <span className="benchmark-perf-label">{perf.label}:</span>
              <span className={`benchmark-perf-value ${perf.return >= 0 ? 'positive' : 'negative'}`}>
                {perf.return >= 0 ? '+' : ''}{perf.return.toFixed(2)}%
              </span>
              <span className={`benchmark-arrow ${perf.return >= 0 ? 'up' : 'down'}`}>
                {perf.return >= 0 ? '▲' : '▼'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Benchmark Toggles - only show if using new benchmarks object */}
      {benchmarks && Object.keys(benchmarks).length > 0 && (
        <div className="mini-chart-benchmarks">
          <span className="benchmarks-label">Compare with:</span>
          {Object.entries(BENCHMARK_CONFIG).map(([key, config]) => {
            const hasBenchmarkData = benchmarks[key] && benchmarks[key].length > 0;
            if (!hasBenchmarkData) return null;

            return (
              <label key={key} className="benchmark-toggle">
                <input
                  type="checkbox"
                  checked={visibleBenchmarks[key]}
                  onChange={() => toggleBenchmark(key)}
                />
                <span
                  className="benchmark-indicator"
                  style={{ backgroundColor: config.color }}
                />
                <span className="benchmark-name">{config.label}</span>
              </label>
            );
          })}
        </div>
      )}

      <ResponsiveContainer width="100%" height={height}>
        {type === 'area' ? (
          <AreaChart data={mergedData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id={`color-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.6} />
                <stop offset="95%" stopColor={color} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#30363d" opacity={0.3} />
            <XAxis
              dataKey="date"
              tickFormatter={formatXAxis}
              stroke="#8b949e"
              style={{ fontSize: '11px' }}
              tick={{ fill: '#8b949e' }}
            />
            <YAxis
              tickFormatter={formatYAxis}
              stroke="#8b949e"
              style={{ fontSize: '11px' }}
              tick={{ fill: '#8b949e' }}
              width={55}
              domain={getYAxisDomain()}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="value"
              name="Portfolio"
              stroke={color}
              strokeWidth={2}
              fillOpacity={1}
              fill={`url(#color-${dataKey})`}
            />

            {/* Multi-benchmark lines */}
            {benchmarks && Object.entries(BENCHMARK_CONFIG).map(([key, config]) => {
              const hasBenchmarkData = benchmarks[key] && benchmarks[key].length > 0;
              if (!hasBenchmarkData || !visibleBenchmarks[key]) return null;

              return (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={config.label}
                  stroke={config.color}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                />
              );
            })}

            {/* Legacy single benchmark */}
            {benchmarkData && benchmarkData.length > 0 && (
              <Line
                type="monotone"
                dataKey="benchmarkValue"
                name={benchmarkLabel}
                stroke="#F59E0B"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
              />
            )}
          </AreaChart>
        ) : (
          <LineChart data={mergedData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#30363d" opacity={0.3} />
            <XAxis
              dataKey="date"
              tickFormatter={formatXAxis}
              stroke="#8b949e"
              style={{ fontSize: '11px' }}
              tick={{ fill: '#8b949e' }}
            />
            <YAxis
              tickFormatter={formatYAxis}
              stroke="#8b949e"
              style={{ fontSize: '11px' }}
              tick={{ fill: '#8b949e' }}
              width={55}
              domain={getYAxisDomain()}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="value"
              name="Portfolio"
              stroke={color}
              strokeWidth={2}
              dot={{ r: 3, fill: color }}
            />

            {/* Multi-benchmark lines */}
            {benchmarks && Object.entries(BENCHMARK_CONFIG).map(([key, config]) => {
              const hasBenchmarkData = benchmarks[key] && benchmarks[key].length > 0;
              if (!hasBenchmarkData || !visibleBenchmarks[key]) return null;

              return (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={config.label}
                  stroke={config.color}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                />
              );
            })}

            {/* Legacy single benchmark */}
            {benchmarkData && benchmarkData.length > 0 && (
              <Line
                type="monotone"
                dataKey="benchmarkValue"
                name={benchmarkLabel}
                stroke="#F59E0B"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
              />
            )}
          </LineChart>
        )}
      </ResponsiveContainer>

      <div className="mini-chart-footer">
        {data.length} data point{data.length !== 1 ? 's' : ''} • Last {Math.min(90, data.length)} days
      </div>
    </div>
  );
};

export default MiniChart;
